/**
 * chatHealthCounters.ts — what the chat can say about its own health, as
 * numbers (2026-09-28, Malaika's report: the chat froze while she typed, a
 * hard refresh did not help, and a send showed "couldn't start this reply").
 *
 * Until now nothing a browser saw reached us: no freeze, no failed Firestore
 * queue, no false watchdog card. Her day was reconstructed from server logs
 * and screenshots. This module counts what the browser sees; the beacon
 * (chatHealthBeacon.ts, loaded only for a signed-in person) sends the counts.
 *
 * What it keeps is COUNTS AND DURATIONS ONLY: how many long tasks and how
 * long, how slow an interaction was, how many sends the watchdog armed and
 * fired, how long a thread took to load, how full local storage is. Never a
 * message, a title, a file name, an address or an id of anything. The event
 * reasons are a closed list; nothing here accepts free text.
 *
 * A LEAF: it imports nothing, so the first download can carry it (it is
 * called from the Firestore boot and the self-heal, which are on it) and the
 * observers start before anything else can freeze the page.
 *
 * Owner decision (Miles, 2026-09-28): a health record for every signed-in
 * person, tagged with the person and the workspace, counts and timings only,
 * Cloud Logging only. A signed-out visitor's counts never leave the page.
 */

/** The closed list of events (server twin: functions/src/telemetry/chatHealthSchema.ts). */
export const CHAT_HEALTH_EVENT_REASONS = [
  'firestore-queue-failed',
  'firestore-self-heal',
  'firestore-heal-declined',
  'firestore-memory-cache',
  'storage-quota',
  'watchdog-fired',
  'send-withdrawn',
  'hydrate-timeout',
  'idb-blocked',
  'long-task-burst',
] as const;
export type ChatHealthEventReason = typeof CHAT_HEALTH_EVENT_REASONS[number];

/** Events that are sent at once (at most one beacon a minute); the rest ride the summary. */
export const SEVERE_REASONS: ReadonlySet<ChatHealthEventReason> = new Set<ChatHealthEventReason>([
  'firestore-queue-failed',
  'firestore-heal-declined',
  'watchdog-fired',
  'hydrate-timeout',
  'long-task-burst',
]);

/** The send step the watchdog was on when it fired (server twin: SEND_PHASES). */
export type ChatHealthSendPhase = 'gate' | 'session' | 'last-stretch' | 'none';

/** The closed list of counters (server twin: COUNTER_KEYS). */
export const CHAT_HEALTH_COUNTER_KEYS = [
  'longTasks',
  'slowInteractions',
  'sendsArmed',
  'watchdogFires',
  'watchdogLateAcks',
  'sendsWithdrawn',
  'hydrates',
  'hydrateTimeouts',
  'idbOpens',
  'idbOpenTimeouts',
  'persists',
  'firestoreQueueFailures',
  'selfHeals',
  'storageQuotaErrors',
  'longTaskMsTotal',
  'longTaskMsMax',
  'interactionMsMax',
  'interactionMsP95',
  'hydrateMsMax',
  'idbOpenMsMax',
  'persistMsMax',
  'uptimeMs',
  'storageChars',
  'threadCacheChars',
] as const;
export type ChatHealthCounterKey = typeof CHAT_HEALTH_COUNTER_KEYS[number];
export type ChatHealthCounters = Partial<Record<ChatHealthCounterKey, number>>;

export interface ChatHealthEvent {
  reason: ChatHealthEventReason;
  atMs: number;
  send?: { phase: ChatHealthSendPhase; msInPhase: number; longTaskMsBeforeFire: number };
}

/** A long task at least this long is a freeze a person feels: sent at once. */
export const LONG_TASK_BURST_MS = 2_000;
/** An interaction (a key, a click) slower than this is counted as slow (the INP "poor" line is 500). */
export const SLOW_INTERACTION_MS = 200;
/** How far back the long tasks before a watchdog fire are summed. */
export const LONG_TASK_LOOKBACK_MS = 90_000;
/** Events kept waiting for the beacon (oldest dropped first). */
const MAX_PENDING_EVENTS = 20;
/** Interaction samples kept for the p95 (a reservoir, newest win). */
const MAX_INTERACTION_SAMPLES = 200;
/** The carry across a self-heal reload (sessionStorage, this tab only). */
export const CHAT_HEALTH_CARRY_KEY = 'prest0_chat_health_carry';
/** The prefix of the chat's cached transcripts (threadPersist.ts THREAD_MSG_KEY_PREFIX). */
const THREAD_CACHE_PREFIX = 'prest0_thread_msgs__';

const counts: Record<string, number> = {};
const pending: ChatHealthEvent[] = [];
const interactionSamples: number[] = [];
const recentLongTasks: Array<{ at: number; ms: number }> = [];
const sendPhases = new Map<string, { phase: ChatHealthSendPhase; since: number }>();
const listeners = new Set<(event: ChatHealthEvent) => void>();
let observersStarted = false;
let version = 0;
let memoryCacheStart = false;

/** Whether this tab started Firestore on its in-memory cache (every beacon says so). */
export function startedOnMemoryCache(): boolean {
  return memoryCacheStart;
}

const clockNow = (): number => {
  try {
    return typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now();
  } catch {
    return Date.now();
  }
};

const bump = (key: ChatHealthCounterKey, by = 1): void => {
  counts[key] = (counts[key] || 0) + by;
  version += 1;
};
const raiseMax = (key: ChatHealthCounterKey, value: number): void => {
  if (!Number.isFinite(value) || value < 0) return;
  if ((counts[key] || 0) < value) {
    counts[key] = value;
    version += 1;
  }
};

/** Count one occurrence. */
export function noteCount(key: 'sendsArmed' | 'watchdogLateAcks' | 'sendsWithdrawn' | 'storageQuotaErrors'): void {
  bump(key);
}

/** Record how long a measured step took (its count and its worst). */
export function noteTiming(what: 'hydrate' | 'idbOpen' | 'persist', ms: number): void {
  if (!Number.isFinite(ms) || ms < 0) return;
  if (what === 'hydrate') { bump('hydrates'); raiseMax('hydrateMsMax', ms); }
  else if (what === 'idbOpen') { bump('idbOpens'); raiseMax('idbOpenMsMax', ms); }
  else { bump('persists'); raiseMax('persistMsMax', ms); }
}

/** Long-task milliseconds in the window before `at` (the page's own freezes). */
export function longTaskMsSince(sinceMs: number): number {
  let total = 0;
  for (const t of recentLongTasks) if (t.at + t.ms >= sinceMs) total += t.ms;
  return Math.round(total);
}

export function noteLongTask(ms: number, at = clockNow()): void {
  if (!Number.isFinite(ms) || ms <= 0) return;
  bump('longTasks');
  bump('longTaskMsTotal', ms);
  raiseMax('longTaskMsMax', ms);
  recentLongTasks.push({ at, ms });
  const cutoff = at - LONG_TASK_LOOKBACK_MS;
  while (recentLongTasks.length && recentLongTasks[0].at + recentLongTasks[0].ms < cutoff) recentLongTasks.shift();
  if (ms >= LONG_TASK_BURST_MS) noteEvent('long-task-burst');
}

export function noteInteraction(ms: number): void {
  if (!Number.isFinite(ms) || ms < 0) return;
  raiseMax('interactionMsMax', ms);
  if (ms >= SLOW_INTERACTION_MS) bump('slowInteractions');
  interactionSamples.push(ms);
  if (interactionSamples.length > MAX_INTERACTION_SAMPLES) interactionSamples.shift();
  version += 1;
}

/** The send step a dispatch is on (the watchdog's steps, useGlobalChat's sendDoorFor). */
export function noteSendPhase(clientTaskId: string, phase: ChatHealthSendPhase): void {
  if (!clientTaskId) return;
  sendPhases.set(clientTaskId, { phase, since: clockNow() });
  if (sendPhases.size > 50) {
    const oldest = sendPhases.keys().next().value;
    if (oldest !== undefined) sendPhases.delete(oldest);
  }
}

/** Forget a send's step (it left, or it ended). */
export function clearSendPhase(clientTaskId: string): void {
  if (clientTaskId) sendPhases.delete(clientTaskId);
}

/**
 * The watchdog fired for a send: counted, and an event carrying the step it
 * was on, how long it had been there, and how much of that time the page
 * itself was frozen (a busy main thread looks exactly like a stuck step).
 */
export function noteWatchdogFired(clientTaskId?: string): void {
  bump('watchdogFires');
  const now = clockNow();
  const at = clientTaskId ? sendPhases.get(clientTaskId) : undefined;
  const since = at ? at.since : now - LONG_TASK_LOOKBACK_MS;
  noteEvent('watchdog-fired', {
    phase: at ? at.phase : 'none',
    msInPhase: Math.max(0, Math.round(now - since)),
    longTaskMsBeforeFire: longTaskMsSince(since),
  });
}

/** Record an event from the closed list; severe ones reach the beacon at once. */
export function noteEvent(reason: ChatHealthEventReason, send?: ChatHealthEvent['send']): void {
  if (!(CHAT_HEALTH_EVENT_REASONS as readonly string[]).includes(reason)) return;
  if (reason === 'firestore-queue-failed') bump('firestoreQueueFailures');
  if (reason === 'firestore-self-heal') bump('selfHeals');
  if (reason === 'hydrate-timeout') bump('hydrateTimeouts');
  if (reason === 'idb-blocked') bump('idbOpenTimeouts');
  if (reason === 'storage-quota') bump('storageQuotaErrors');
  if (reason === 'send-withdrawn') bump('sendsWithdrawn');
  if (reason === 'firestore-memory-cache') memoryCacheStart = true;
  const event: ChatHealthEvent = { reason, atMs: Date.now(), ...(reason === 'watchdog-fired' && send ? { send } : {}) };
  pending.push(event);
  if (pending.length > MAX_PENDING_EVENTS) pending.shift();
  version += 1;
  listeners.forEach((listener) => {
    try { listener(event); } catch { /* a listener never breaks the page */ }
  });
}

/** Called for every new event (the beacon's hook for sending severe ones at once). */
export function onChatHealthEvent(listener: (event: ChatHealthEvent) => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** How many events are waiting for the beacon. */
export function pendingEventCount(): number {
  return pending.length;
}

/** Take the waiting events (the beacon sends them). */
export function takePendingEvents(): ChatHealthEvent[] {
  return pending.splice(0, pending.length);
}

/** Put events back after a send that could not leave (kept, oldest dropped past the cap). */
export function returnPendingEvents(events: ChatHealthEvent[]): void {
  pending.unshift(...events);
  while (pending.length > MAX_PENDING_EVENTS) pending.shift();
}

/** Changes whenever a counter or event changes: the beacon sends a summary only after one. */
export function chatHealthVersion(): number {
  return version;
}

function p95(samples: number[]): number | undefined {
  if (samples.length < 5) return undefined;
  const sorted = [...samples].sort((a, b) => a - b);
  return sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95))];
}

/** Local storage size, in characters (the browser's quota is counted in them). */
export function scanStorageChars(): { storageChars: number; threadCacheChars: number } | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    let storageChars = 0;
    let threadCacheChars = 0;
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key === null) continue;
      const size = key.length + (localStorage.getItem(key)?.length ?? 0);
      storageChars += size;
      if (key.startsWith(THREAD_CACHE_PREFIX)) threadCacheChars += size;
    }
    return { storageChars, threadCacheChars };
  } catch {
    return null;
  }
}

/** The counters as numbers, rounded (every key from the closed list, only those). */
export function chatHealthSnapshot(): ChatHealthCounters {
  const out: ChatHealthCounters = {};
  for (const key of CHAT_HEALTH_COUNTER_KEYS) {
    const value = counts[key];
    if (typeof value === 'number' && Number.isFinite(value)) out[key] = Math.round(value);
  }
  const p = p95(interactionSamples);
  if (p !== undefined) out.interactionMsP95 = Math.round(p);
  out.uptimeMs = Math.round(clockNow());
  const storage = scanStorageChars();
  if (storage) {
    out.storageChars = storage.storageChars;
    out.threadCacheChars = storage.threadCacheChars;
  }
  return out;
}

/**
 * Before a self-heal reload: keep this tab's waiting events in sessionStorage
 * so the next page can send them (the heal is exactly what we most need to
 * hear about, and the reload would lose it).
 */
export function carryAcrossReload(): void {
  try {
    if (typeof sessionStorage === 'undefined') return;
    const events = pending.slice(-MAX_PENDING_EVENTS);
    sessionStorage.setItem(CHAT_HEALTH_CARRY_KEY, JSON.stringify({ events }));
  } catch { /* a full or blocked store loses the carry, never the page */ }
}

function restoreCarry(): void {
  try {
    if (typeof sessionStorage === 'undefined') return;
    const raw = sessionStorage.getItem(CHAT_HEALTH_CARRY_KEY);
    if (!raw) return;
    sessionStorage.removeItem(CHAT_HEALTH_CARRY_KEY);
    const parsed = JSON.parse(raw) as { events?: unknown };
    if (!Array.isArray(parsed.events)) return;
    for (const e of parsed.events.slice(-MAX_PENDING_EVENTS)) {
      const reason = (e as { reason?: unknown })?.reason;
      const atMs = (e as { atMs?: unknown })?.atMs;
      if (typeof reason !== 'string' || !(CHAT_HEALTH_EVENT_REASONS as readonly string[]).includes(reason)) continue;
      pending.push({ reason: reason as ChatHealthEventReason, atMs: typeof atMs === 'number' ? atMs : Date.now() });
    }
    version += 1;
  } catch { /* a malformed carry is dropped */ }
}

/**
 * Start watching the page (once): long tasks and slow interactions through
 * PerformanceObserver (durations only; the entries' targets and names are
 * never read), and restore a carry from a self-heal reload. Never throws.
 */
export function startChatHealthObservers(): void {
  if (observersStarted) return;
  observersStarted = true;
  restoreCarry();
  try {
    if (typeof PerformanceObserver === 'undefined') return;
    const supported = (PerformanceObserver as unknown as { supportedEntryTypes?: readonly string[] }).supportedEntryTypes ?? [];
    if (supported.includes('longtask')) {
      new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) noteLongTask(entry.duration, entry.startTime);
      }).observe({ type: 'longtask', buffered: true });
    }
    if (supported.includes('event')) {
      new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          // One interaction may produce several entries (keydown, keyup…); only those with an id are a person's.
          if ((entry as unknown as { interactionId?: number }).interactionId) noteInteraction(entry.duration);
        }
      }).observe({ type: 'event', buffered: true, durationThreshold: 40 } as PerformanceObserverInit);
    }
  } catch { /* an old browser simply reports less */ }
}

/** Tests only: back to a fresh page. */
export function resetChatHealthForTests(): void {
  for (const k of Object.keys(counts)) delete counts[k];
  pending.length = 0;
  interactionSamples.length = 0;
  recentLongTasks.length = 0;
  sendPhases.clear();
  listeners.clear();
  observersStarted = false;
  version = 0;
  memoryCacheStart = false;
}
